import { useRef, useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { useAuth } from "@clerk/react";
import { Check, X, Heart, Play, Home, Film, Flame } from "lucide-react";
import { toast } from "sonner";
import Nav from "@/components/Nav";
import Mascot from "@/components/Mascot";
import ShareButton from "@/components/ShareButton";
import { publicApi, makeApi } from "@/lib/api";
import { loadAdsense, showRewardedAd } from "@/lib/ads";
import { badgeById } from "@/lib/badges";
import { isAndroidApp } from "@/lib/platform";

export default function QuizPlay() {
  const { id } = useParams();
  const { getToken, isSignedIn } = useAuth();
  const api = useMemo(() => makeApi(getToken), [getToken]);

  const [quiz, setQuiz] = useState(null);
  const [idx, setIdx] = useState(0);
  const [picked, setPicked] = useState(null);
  const [score, setScore] = useState(0);
  const [done, setDone] = useState(false);
  const [me, setMe] = useState(null);
  const [feedback, setFeedback] = useState(null); // "correct" | "wrong"
  const [showAdModal, setShowAdModal] = useState(false);
  const [adRunning, setAdRunning] = useState(false);
  const [result, setResult] = useState(null);
  const postedRef = useRef(false);

  useEffect(() => {
    api.get(`/quizzes/${id}`).then((r) => setQuiz(r.data)).catch(() => setQuiz({ missing: true }));
    publicApi.get("/billing/config").then((r) => loadAdsense(r.data.adsense_client_id)).catch((e) => console.error("Ad config failed", e));
  }, [id, api]);

  useEffect(() => {
    if (!isSignedIn) return;
    api.get("/me").then((r) => setMe(r.data)).catch((e) => console.error("Failed to load profile", e));
  }, [isSignedIn, api]);

  const q = quiz?.questions?.[idx];
  const total = quiz?.questions?.length || 0;
  const progress = total ? ((idx + (picked !== null ? 1 : 0)) / total) * 100 : 0;

  const choose = async (i) => {
    if (picked !== null || !q || !me) return;
    if (!me.subscribed && me.lives <= 0) {
      setShowAdModal(true);
      return;
    }
    setPicked(i);
    const correct = i === q.correct_index;
    setFeedback(correct ? "correct" : "wrong");
    if (correct) setScore((s) => s + 1);
    else if (!me.subscribed) {
      try {
        const r = await api.post("/me/lives", { action: "lose" });
        setMe((m) => ({ ...m, lives: r.data.lives }));
      } catch (e) {
        console.error("Failed to update lives", e);
        toast.error("Couldn't save that life change. Check your connection.");
      }
    }
  };

  const next = () => {
    setFeedback(null);
    setPicked(null);
    if (idx + 1 >= total) setDone(true);
    else setIdx((i) => i + 1);
  };

  const grantLife = async () => {
    const r = await api.post("/me/lives", { action: "earn_ad" });
    setMe((m) => ({ ...m, lives: r.data.lives }));
    toast.success("You earned +1 life!");
  };

  const watchAd = async () => {
    setAdRunning(true);
    try {
      const outcome = await showRewardedAd();
      if (outcome === "viewed") await grantLife();
      else if (outcome === "dismissed") toast.error("Watch the whole ad to earn a life.");
      else {
        await new Promise((r) => setTimeout(r, 1600));
        await grantLife();
      }
    } catch (e) {
      console.error("Rewarded ad failed", e);
      toast.error("Couldn't add your life. Please try again.");
    }
    setAdRunning(false);
    setShowAdModal(false);
  };

  useEffect(() => {
    if (!done || postedRef.current || !isSignedIn) return;
    postedRef.current = true;
    api.post("/scores", { quiz_id: id, score, total }).then((r) => {
      setResult(r.data);
      (r.data.new_badges || []).forEach((b) => toast.success(`New badge: ${badgeById(b)?.name || b}!`));
    }).catch((e) => {
      console.error("Failed to save score", e);
      toast.error("Your score couldn't be saved.");
    });
  }, [done, isSignedIn, api, id, score, total]);

  if (!isSignedIn) return null;
  if (!quiz) return <><Nav /><p className="p-8">Loading…</p></>;
  if (quiz.missing) return (
    <>
      <Nav />
      <main className="max-w-xl mx-auto px-4 py-16 text-center">
        <div className="flex justify-center"><Mascot size={150} /></div>
        <div className="sticker-card p-8 mt-6">
          <h1 className="text-2xl font-black">Hmm, this quiz isn't available</h1>
          <p className="mt-2 text-slate-700" data-testid="quiz-not-found">It may have been removed, or it's only for students in a teacher's class. Join with your class code first.</p>
          <div className="mt-6 flex gap-3 justify-center flex-wrap">
            <Link to="/classes" data-testid="not-found-join-class" className="px-5 py-2.5 rounded-full border-[3px] border-[#374151] bg-[var(--tp-primary)] text-white font-black shadow-[0_4px_0_0_#374151]">Join a class</Link>
            <Link to="/play" className="px-5 py-2.5 rounded-full border-[3px] border-[#374151] bg-white font-black shadow-[0_4px_0_0_#374151]">Browse quizzes</Link>
          </div>
        </div>
      </main>
    </>
  );

  if (done) {
    const pct = total ? Math.round((score / total) * 100) : 0;
    const message = pct >= 80 ? "Legendary roll!" : pct >= 50 ? "Nice work!" : "Keep rolling!";
    return (
      <>
        <Nav lives={me?.lives} key={result ? "after-score" : "before-score"} />
        <main className="max-w-2xl mx-auto px-4 py-16 text-center">
          <div className="flex justify-center mb-6"><Mascot size={180} /></div>
          <h1 className="text-5xl font-black tracking-tight">{message}</h1>
          <div className="mt-8 sticker-card p-8 inline-block">
            <p className="text-slate-600 font-bold">Final score</p>
            <p className="text-6xl font-black text-[var(--tp-success)]" data-testid="final-score">{score} / {total}</p>
            <p className="text-slate-700 mt-2">{pct}% correct</p>
          </div>
          {result && (
            <div className="mt-6 flex flex-col items-center gap-3" data-testid="streak-result">
              <p className="font-black text-[#F97316] text-lg inline-flex items-center gap-1"><Flame size={20} fill="#F97316" strokeWidth={2.5} /> {result.streak}-day streak</p>
              {(result.new_badges || []).map((b) => {
                const meta = badgeById(b);
                return (
                  <div key={b} className="sticker px-5 py-3 font-black bounce-correct" style={{ background: meta?.color || "var(--tp-accent)" }} data-testid={`new-badge-${b}`}>
                    New badge unlocked: {meta?.name} — {meta?.blurb}
                  </div>
                );
              })}
            </div>
          )}
          <div className="mt-10 flex gap-3 justify-center flex-wrap">
            <ShareButton path={`/quiz/${id}`} label="Challenge friends" testid="share-quiz-result" title={quiz.title} />
            <Link to={`/category/${quiz.category_id}`} data-testid="play-another"
              className="px-6 py-3 rounded-full border-[3px] border-[#374151] bg-white font-black shadow-[0_4px_0_0_#374151] active:translate-y-1 active:shadow-none inline-flex items-center gap-2">
              <Play size={18} strokeWidth={2.5} /> Play another
            </Link>
            <Link to="/leaderboard" data-testid="see-leaderboard"
              className="px-6 py-3 rounded-full border-[3px] border-[#374151] bg-[var(--tp-primary)] text-white font-black shadow-[0_4px_0_0_#374151] active:translate-y-1 active:shadow-none">
              See leaderboard
            </Link>
            <Link to="/play" className="px-6 py-3 rounded-full border-[3px] border-[#374151] bg-white font-black shadow-[0_4px_0_0_#374151] active:translate-y-1 active:shadow-none inline-flex items-center gap-2">
              <Home size={18} strokeWidth={2.5} /> Dashboard
            </Link>
          </div>
        </main>
      </>
    );
  }

  const optionColors = ["var(--tp-primary)", "var(--tp-success)", "var(--tp-accent)", "#FF4B4B"];

  return (
    <>
      <Nav lives={me?.lives} />
      <main className="max-w-3xl mx-auto px-4 py-8">
        <div className="flex items-center gap-3">
          <div className="flex-1 h-4 bg-slate-200 border-[2px] border-[#374151] rounded-full overflow-hidden">
            <div className="h-full bg-[var(--tp-success)] transition-all" style={{ width: `${progress}%` }} />
          </div>
          <span className="font-black text-sm shrink-0">{idx + 1} / {total}</span>
          <ShareButton path={`/quiz/${id}`} label="Share" testid="share-quiz" title={quiz.title} />
        </div>
        <h1 className="mt-4 text-lg font-black text-slate-600" data-testid="quiz-title">{quiz.title}</h1>

        <div className={`sticker-card mt-4 p-6 md:p-10 ${feedback === "correct" ? "bounce-correct" : ""} ${feedback === "wrong" ? "shake-wrong" : ""}`}>
          <p className="text-slate-500 font-bold text-sm uppercase tracking-wider">
            {q.type === "mcq" ? "Multiple choice" : "True or False"}
          </p>
          <h2 className="mt-3 text-2xl md:text-3xl font-black leading-tight" data-testid="question-text">{q.text}</h2>

          <div className={`mt-8 grid gap-3 ${q.type === "tf" ? "grid-cols-1 sm:grid-cols-2" : "grid-cols-1 sm:grid-cols-2"}`}>
            {q.options.map((opt, i) => {
              const isPicked = picked === i;
              const isCorrect = picked !== null && i === q.correct_index;
              const isWrongPick = isPicked && i !== q.correct_index;
              let bg = "#FFFFFF", color = "#1F2937";
              if (picked === null) {
                bg = "#FFFFFF";
              } else if (isCorrect) {
                bg = "var(--tp-success)"; color = "#FFFFFF";
              } else if (isWrongPick) {
                bg = "#FF4B4B"; color = "#FFFFFF";
              }
              return (
                <button key={i} onClick={() => choose(i)} disabled={picked !== null}
                  data-testid={`answer-${i}`}
                  className="text-left px-5 py-4 rounded-2xl border-[3px] border-[#374151] font-black text-lg shadow-[0_4px_0_0_#374151] active:translate-y-1 active:shadow-none disabled:cursor-default"
                  style={{ background: bg, color }}>
                  <span className="inline-flex items-center gap-2">
                    {picked !== null && isCorrect && <Check strokeWidth={3} />}
                    {picked !== null && isWrongPick && <X strokeWidth={3} />}
                    {opt}
                  </span>
                </button>
              );
            })}
          </div>

          {picked !== null && (
            <div className="mt-8 flex justify-end">
              <button onClick={next} data-testid="next-question"
                className="px-6 py-3 rounded-full border-[3px] border-[#374151] bg-[var(--tp-primary)] text-white font-black shadow-[0_4px_0_0_#374151] active:translate-y-1 active:shadow-none">
                {idx + 1 >= total ? "See results" : "Next question"}
              </button>
            </div>
          )}
        </div>

        <div className="mt-6 flex items-center gap-2 text-slate-700">
          <Heart size={18} fill="#FF4B4B" color="#FF4B4B" strokeWidth={2.5} />
          <span className="font-bold">Lives: {me?.subscribed ? "Unlimited (Supporter)" : me?.lives ?? "-"}</span>
          {me && !me.subscribed && me.lives === 0 && (
            <button onClick={() => setShowAdModal(true)} data-testid="watch-ad-btn"
              className="ml-auto px-3 py-1.5 rounded-full border-[2px] border-[#374151] bg-[var(--tp-accent)] font-black text-sm shadow-[0_3px_0_0_#374151] active:translate-y-[3px] active:shadow-none">
              Watch ad • +1 life
            </button>
          )}
        </div>
      </main>

      {showAdModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="ad-modal">
          <div className="sticker-card p-8 max-w-md w-full text-center">
            <Film size={48} strokeWidth={2.5} className="mx-auto text-[var(--tp-primary)]" />
            <h3 className="mt-4 text-2xl font-black">Out of lives!</h3>
            <p className="mt-2 text-slate-700">Watch a short ad to earn +1 life and keep playing.</p>
            {isAndroidApp() ? null : (
            <Link to="/pricing" className="mt-2 inline-block text-sm font-bold text-[var(--tp-primary)] underline" data-testid="ad-modal-go-pro">
              Or go ad-free with unlimited lives for $2.99/mo
            </Link>
            )}
            {adRunning ? (
              <div className="mt-6 p-6 bg-slate-900 text-white rounded-2xl border-[3px] border-[#374151]">
                <p className="font-black">Ad playing…</p>
              </div>
            ) : (
              <div className="mt-6 flex gap-3 justify-center">
                <button onClick={() => setShowAdModal(false)} className="px-4 py-2 rounded-full border-[3px] border-[#374151] bg-white font-black shadow-[0_4px_0_0_#374151] active:translate-y-1 active:shadow-none">
                  Not now
                </button>
                <button onClick={watchAd} data-testid="confirm-watch-ad"
                  className="px-5 py-2 rounded-full border-[3px] border-[#374151] bg-[var(--tp-success)] text-white font-black shadow-[0_4px_0_0_#374151] active:translate-y-1 active:shadow-none">
                  Watch ad
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}
